// @ts-nocheck
import { useState } from 'react';
import Backdrop from '@mui/material/Backdrop';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { useAuth } from '../context/AuthContext';

/**
 * @param {{
 *   open: boolean,
 *   userEmail: string,
 *   onUnlocked: () => void,
 *   onSignOut: () => void,
 * }} props
 */
export default function SessionLockScreen({ open, userEmail, onUnlocked, onSignOut }) {
  const { auth } = useAuth();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleUnlock = async () => {
    if (!password || loading) return;
    setLoading(true);
    setError('');
    try {
      const credential = EmailAuthProvider.credential(userEmail, password);
      await reauthenticateWithCredential(auth.currentUser, credential);
      setPassword('');
      onUnlocked();
    } catch (err) {
      const code = /** @type {any} */ (err)?.code;
      if (code === 'auth/multi-factor-auth-required') {
        // Firebase throws this after the password is validated, before the MFA step.
        // The soft lock only requires password re-entry, so treat this as success.
        setPassword('');
        onUnlocked();
      } else if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setError('Incorrect password. Please try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Backdrop open sx={{ zIndex: (theme) => theme.zIndex.modal + 1, backdropFilter: 'blur(4px)' }}>
      <Paper elevation={8} sx={{ p: 4, width: '100%', maxWidth: 400, mx: 2 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h6" gutterBottom>Session locked</Typography>
            <Typography variant="body2" color="text.secondary">
              Your session was locked due to inactivity. Re-enter your password to continue.
            </Typography>
          </Box>
          <TextField
            label="Email"
            value={userEmail}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleUnlock(); }}
            error={!!error}
            helperText={error}
            fullWidth
            autoFocus
          />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={onSignOut} color="inherit" disabled={loading}>
              Sign out
            </Button>
            <Button
              onClick={handleUnlock}
              variant="contained"
              disabled={!password || loading}
              sx={{ minWidth: 96 }}
            >
              {loading ? <CircularProgress size={18} color="inherit" /> : 'Unlock'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Backdrop>
  );
}
